import React, { useState } from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  PasswordInput,
  BooleanInput,
  required,
  useTranslate,
  useNotify,
  useRedirect,
  SaveButton,
  DeleteButton,
  Toolbar,
  useRecordContext,
  DateField,
} from 'react-admin';
import { Box, Typography, Alert, Button, CircularProgress, Chip, Card, CardContent, IconButton } from '@mui/material';
import { CloudQueue, CheckCircle, VpnKey, ArrowBack, Settings, Info } from '@mui/icons-material';
import { tokenManager } from '../../utils/tokenManager';
import { API_CONFIG } from '../../config/api';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

// The form uses snake_case sources (e.g. `supabase_url`) but the backend Zod
// schema validates camelCase fields (e.g. `supabaseUrl`). Map camelCase → snake
// so server errors land on the correct form inputs.
const SUPABASE_CAMEL_TO_FORM: Record<string, string> = {
    supabaseUrl: 'supabase_url',
    serviceKey: 'service_key',
    databaseUrl: 'database_url',
    isActive: 'is_active',
};

// The i18n file uses camelCase field keys; map the form's snake_case source
// back so the label lookup resolves.
const SUPABASE_FORM_TO_LABEL: Record<string, string> = {
    supabase_url: 'supabaseUrl',
    service_key: 'serviceKey',
    database_url: 'databaseUrl',
    is_active: 'isActive',
};

const SUPABASE_CONNECTION_KNOWN_FIELDS = new Set([
    'name', 'description', 'supabase_url', 'service_key',
    'database_url', 'environment', 'is_active',
]);

const mapSupabaseConnectionBackendPath = (path: string): string => {
    const stripped = path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');
    return SUPABASE_CAMEL_TO_FORM[stripped] || stripped;
};

const supabaseConnectionFieldLabelKey = (field: string): string => {
    if (!SUPABASE_CONNECTION_KNOWN_FIELDS.has(field)) return field;
    const labelKey = SUPABASE_FORM_TO_LABEL[field] || field;
    return `resources.supabase-connections.fields.${labelKey}`;
};

const EditToolbar = () => {
  const translate = useTranslate();

  return (
    <Toolbar
      sx={{
        backgroundColor: '#FAFAFA',
        borderTop: '1px solid #E0E0E0',
        px: { xs: 2, sm: 3 },
        py: 2,
        mt: { xs: 2, sm: 4 },
        justifyContent: 'space-between',
        '& .MuiButton-root': {
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          px: 3,
          py: 1.5,
        },
      }}
    >
      <SaveButton
        variant="contained"
        sx={{
          backgroundColor: '#00C853',
          '&:hover': {
            backgroundColor: '#00A843',
            boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
          },
        }}
      />
      <DeleteButton
        mutationMode="pessimistic"
        sx={{
          color: '#F44336',
          '&:hover': {
            backgroundColor: '#FFF3F0',
          },
        }}
      />
    </Toolbar>
  );
};

const PageHeader = () => {
  const redirect = useRedirect();
  const translate = useTranslate();
  const record = useRecordContext();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 4 } }}>
      <IconButton
        onClick={() => redirect('/supabase-connections')}
        size="large"
        sx={{
          backgroundColor: '#FFFFFF',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          '&:hover': {
            backgroundColor: '#F5F5F5',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          },
        }}
        title={translate('ra.action.back')}
      >
        <ArrowBack />
      </IconButton>
      <Box sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2.125rem' }, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
            {translate('resources.supabase-connections.edit.title')}
          </Typography>
          {record && (
            <Chip
              label={translate(`resources.supabase-connections.environments.${record.environment}`) || record.environment}
              size="small"
              sx={{
                backgroundColor:
                  record.environment === 'production'
                    ? '#FFEBEE'
                    : record.environment === 'staging'
                    ? '#FFF3E0'
                    : record.environment === 'uat'
                    ? '#E8F5E9'
                    : '#E3F2FD',
                color:
                  record.environment === 'production'
                    ? '#C62828'
                    : record.environment === 'staging'
                    ? '#F57C00'
                    : record.environment === 'uat'
                    ? '#2E7D32'
                    : '#1565C0',
                fontWeight: 500,
              }}
            />
          )}
        </Box>
        <Typography variant="body1" color="text.secondary">
          {translate('resources.supabase-connections.edit.subtitle')}
        </Typography>
      </Box>
    </Box>
  );
};

const CredentialSection = () => {
  const translate = useTranslate();
  const record = useRecordContext();
  const [showCredentials, setShowCredentials] = useState(false);

  return (
    <>
      <Alert severity="warning" sx={{ mb: 3 }}>
        {translate('resources.supabase-connections.warnings.credentialUpdate')}
      </Alert>

      {!showCredentials ? (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {translate('resources.supabase-connections.info.existingCredentials')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            {record?.encrypted_service_key && (
              <Chip
                size="small"
                icon={<VpnKey />}
                label={translate('resources.supabase-connections.badges.serviceKey')}
                sx={{
                  backgroundColor: '#E8F5E8',
                  color: '#1B5E20',
                }}
              />
            )}
            {record?.database_url_encrypted && (
              <Chip
                size="small"
                icon={<VpnKey />}
                label={translate('resources.supabase-connections.badges.databaseUrl')}
                sx={{
                  backgroundColor: '#FFF3E0',
                  color: '#E65100',
                }}
              />
            )}
          </Box>
          <Button
            variant="outlined"
            onClick={() => setShowCredentials(true)}
            startIcon={<VpnKey />}
          >
            {translate('resources.supabase-connections.actions.updateCredentials')}
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextInput
            source="supabase_url"
            label={translate('resources.supabase-connections.fields.supabaseUrl')}
            validate={required()}
            fullWidth
            placeholder="https://xxxxxxxxxxxx.supabase.co"
            helperText={translate('resources.supabase-connections.helpers.supabaseUrl')}
          />

          <PasswordInput
            source="service_key"
            label={translate('resources.supabase-connections.fields.serviceKey')}
            fullWidth
            helperText={translate('resources.supabase-connections.helpers.updateServiceKey')}
            placeholder={translate('resources.supabase-connections.placeholders.leaveEmpty')}
          />


          <PasswordInput
            source="database_url"
            label={translate('resources.supabase-connections.fields.databaseUrl')}
            fullWidth
            placeholder="postgresql://postgres:password@db.xxxxxxxxxxxx.supabase.co:5432/postgres"
            helperText={translate('resources.supabase-connections.helpers.updateDatabaseUrl')}
          />

          <Button
            variant="text"
            onClick={() => setShowCredentials(false)}
            sx={{ mt: 1 }}
          >
            {translate('resources.supabase-connections.actions.cancelUpdate')}
          </Button>
        </Box>
      )}
    </>
  );
};

const TestConnectionButton = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const record = useRecordContext();
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleTestConnection = async () => {
    if (!record?.id) {
      notify('No record ID found', { type: 'error' });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const accessToken = await tokenManager.getAccessToken();
      if (!accessToken) {
        notify('Authentication required. Please log in again.', { type: 'error' });
        setTesting(false);
        return;
      }

      const response = await fetch(
        `${API_CONFIG.baseURL}/api/backend/supabase-connections/${record.id}/test`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      const result = await response.json();

      if (result.success) {
        setTestResult({ success: true, message: translate('resources.supabase-connections.messages.connectionSuccess') });
        notify('Connection test successful', { type: 'success' });
      } else {
        setTestResult({ success: false, message: result.error || translate('resources.supabase-connections.messages.connectionFailed') });
        notify(`Connection test failed: ${result.error}`, { type: 'error' });
      }
    } catch (error: any) {
      setTestResult({ success: false, message: error.message || 'Unknown error' });
      notify(`Connection test error: ${error.message || 'Unknown error'}`, { type: 'error' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Box sx={{ mt: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
      <Button
        variant="outlined"
        onClick={handleTestConnection}
        disabled={testing}
        startIcon={testing ? <CircularProgress size={16} /> : <CheckCircle />}
      >
        {testing
          ? translate('resources.supabase-connections.actions.testing')
          : translate('resources.supabase-connections.actions.testConnection')}
      </Button>

      {testResult && (
        <Alert
          severity={testResult.success ? 'success' : 'error'}
          sx={{ flex: 1 }}
          onClose={() => setTestResult(null)}
        >
          {testResult.message}
        </Alert>
      )}
    </Box>
  );
};

export const SupabaseConnectionsEdit = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});

  return (
    <Edit
      title={false}
      actions={false}
      component="div"
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          setServerErrors({});
          notify('ra.notification.updated', { type: 'success', messageArgs: { smart_count: 1 } });
          redirect('list', 'supabase-connections');
        },
        onError: (error: any) => {
          const apiError = error?.body || error;
          const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
          const fieldErrors: Record<string, string> = {};
          const summaryLines: string[] = [];
          if (rawErrors) {
            for (const [path, msgs] of Object.entries(rawErrors)) {
              const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
              const translated = translateServerMessage(rawMsg, translate);
              const field = mapSupabaseConnectionBackendPath(path);
              fieldErrors[field] = translated;
              const label = translate(supabaseConnectionFieldLabelKey(field), { _: field });
              summaryLines.push(`• ${label}: ${translated}`);
            }
          }
          setServerErrors(fieldErrors);

          const headerKey = apiError?.errorCode
            ? `errors.${apiError.errorCode}`
            : 'validation.formHasErrors';
          const header = translate(headerKey, { _: apiError?.message || 'Error' });

          if (summaryLines.length > 0) {
            notify(`${header}\n${summaryLines.join('\n')}`, {
              type: 'error',
              multiLine: true,
              autoHideDuration: 10000,
            });
          } else {
            notify(header, { type: 'error', autoHideDuration: 6000 });
          }
        },
      }}
      sx={{
        backgroundColor: 'transparent',
        '& .RaEdit-main': {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <SimpleForm
        toolbar={<EditToolbar />}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
          '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
            px: { xs: 0, sm: 2 },
            py: { xs: 1, sm: 2 },
          },
          padding: { xs: 0, sm: 'inherit' },
        }}
      >
        <ServerErrorBinder errors={serverErrors} />
        <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', '& .MuiFormControl-root': { width: '100%' }, pb: { xs: 10, sm: 2 } }}>
          <PageHeader />

          {/* Basic Information Section */}
          <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CloudQueue sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {translate('resources.supabase-connections.sections.basic')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextInput
                  source="name"
                  label={translate('resources.supabase-connections.fields.name')}
                  validate={required()}
                  fullWidth
                  helperText={translate('resources.supabase-connections.helpers.name')}
                />

                <SelectInput
                  source="environment"
                  label={translate('resources.supabase-connections.fields.environment')}
                  choices={[
                    { id: 'production', name: 'Production' },
                    { id: 'staging', name: 'Staging' },
                    { id: 'development', name: 'Development' },
                    { id: 'uat', name: 'UAT' },
                  ]}
                  validate={required()}
                  fullWidth
                  helperText={translate('resources.supabase-connections.helpers.environment')}
                />

                <TextInput
                  source="description"
                  label={translate('resources.supabase-connections.fields.description')}
                  fullWidth
                  multiline
                  rows={2}
                  helperText={translate('resources.supabase-connections.helpers.description')}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Credentials Section */}
          <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <VpnKey sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {translate('resources.supabase-connections.sections.credentials')}
                </Typography>
              </Box>

              <CredentialSection />

              <TestConnectionButton />
            </CardContent>
          </Card>

          {/* Settings Section */}
          <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Settings sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {translate('resources.supabase-connections.sections.settings')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <BooleanInput
                  source="is_active"
                  label={translate('resources.supabase-connections.fields.isActive')}
                  helperText={translate('resources.supabase-connections.helpers.isActive')}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Metadata Section */}
          <Card sx={{ overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Info sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {translate('resources.supabase-connections.sections.metadata')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3 }, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {translate('resources.supabase-connections.fields.createdAt')}
                  </Typography>
                  <DateField
                    source="created_at"
                    locales="en-US"
                    options={{
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    }}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {translate('resources.supabase-connections.fields.updatedAt')}
                  </Typography>
                  <DateField
                    source="updated_at"
                    locales="en-US"
                    options={{
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </SimpleForm>
    </Edit>
  );
};